import { useEffect, useState } from 'react'
import { errBanner, okBanner } from '@ui/admin'
import { btn, btnBase, btnSize, cx, hint } from '@ui/classes'
import {
  downloadAndInstallAppUpdate,
  getAppUpdateInfo,
  isNativeAppUpdate,
  openAppUpdateInstallSettings,
  type AppUpdateInfo,
  type AppUpdateResult
} from '@core/native/appUpdate'

const UPDATE_MANIFEST_URL = 'https://api.pospoint.ru/downloads/latest.json'

interface LatestManifest {
  versionCode: number
  versionName: string
  url: string
  sha256: string
  size?: number
  notes?: string
  publishedAt?: string
}

type Message = { tone: 'ok' | 'err' | 'hint'; text: string }

function formatSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '—'
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`
}

/**
 * Обновление Android-приложения.
 *
 * APK скачивается native-плагином, проверяется по SHA-256, package,
 * versionCode и подписи. Последний шаг — системный установщик Android:
 * полностью безлюдная установка на обычном POS невозможна.
 */
export function AppUpdateCard() {
  const native = isNativeAppUpdate()
  const [info, setInfo] = useState<AppUpdateInfo | null>(null)
  const [latest, setLatest] = useState<LatestManifest | null>(null)
  const [checking, setChecking] = useState(false)
  const [installing, setInstalling] = useState(false)
  const [permissionRequired, setPermissionRequired] = useState(false)
  const [message, setMessage] = useState<Message | null>(null)

  useEffect(() => {
    if (!native) return
    void getAppUpdateInfo()
      .then(setInfo)
      .catch((error) => {
        setMessage({ tone: 'err', text: error instanceof Error ? error.message : String(error) })
      })
  }, [native])

  const check = async () => {
    if (!native) {
      setMessage({ tone: 'hint', text: 'Обновление доступно только в Android-приложении' })
      return
    }
    setChecking(true)
    setPermissionRequired(false)
    setMessage(null)
    try {
      const response = await fetch(UPDATE_MANIFEST_URL, { cache: 'no-store' })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const manifest = (await response.json()) as LatestManifest
      if (!manifest.versionCode || !manifest.versionName || !manifest.url || !manifest.sha256) {
        throw new Error('Некорректный latest.json')
      }
      setLatest(manifest)
      if (info && manifest.versionCode <= info.currentVersionCode) {
        setMessage({ tone: 'ok', text: `Установлена актуальная версия ${info.currentVersionName}` })
      } else {
        setMessage({ tone: 'ok', text: `Доступна версия ${manifest.versionName}` })
      }
    } catch (error) {
      setMessage({
        tone: 'err',
        text: error instanceof Error ? error.message : 'Не удалось проверить обновление'
      })
    } finally {
      setChecking(false)
    }
  }

  const install = async () => {
    if (!native || !latest || !info) return
    const confirmed = window.confirm(
      'Перед обновлением завершите продажу и фискальную операцию. ' +
        'Приложение будет закрыто установщиком. Продолжить?'
    )
    if (!confirmed) return
    setInstalling(true)
    setMessage(null)
    try {
      const result: AppUpdateResult = await downloadAndInstallAppUpdate({
        url: latest.url,
        versionCode: latest.versionCode,
        versionName: latest.versionName,
        sha256: latest.sha256
      })
      if (result.status === 'permission_required') {
        setPermissionRequired(true)
        setMessage({
          tone: 'hint',
          text: 'Разрешите установку неизвестных приложений для POSPoint и повторите попытку.'
        })
        return
      }
      if (result.status !== 'installer_opened') {
        throw new Error(result.error || `Статус обновления: ${result.status}`)
      }
      setMessage({
        tone: 'ok',
        text: 'APK скачан и проверен. Открыт системный установщик — нажмите «Установить».'
      })
    } catch (error) {
      setMessage({
        tone: 'err',
        text: error instanceof Error ? error.message : 'Не удалось скачать обновление'
      })
    } finally {
      setInstalling(false)
    }
  }

  const openSettings = async () => {
    try {
      await openAppUpdateInstallSettings()
    } catch (error) {
      setMessage({
        tone: 'err',
        text: error instanceof Error ? error.message : 'Не удалось открыть настройки установки'
      })
    }
  }

  const updateAvailable = Boolean(
    native && info && latest && latest.versionCode > info.currentVersionCode
  )

  return (
    <div>
      <p className={hint}>
        APK скачивается по HTTPS, проверяется SHA-256, package, versionCode и подпись.
        Финальную установку подтверждает пользователь в системном установщике Android.
      </p>

      {info && (
        <div className="mt-2 space-y-1 text-small">
          <div>Текущая версия: <strong>{info.currentVersionName}</strong></div>
          <div>versionCode: {info.currentVersionCode}</div>
          <div>Package: {info.packageName}</div>
          <div>
            Установка из приложения: {info.canInstallPackages ? 'разрешена' : 'требуется разрешение'}
          </div>
        </div>
      )}

      {!native && (
        <p className={hint}>
          В web/Electron обновление доступно вручную по ссылке release-страницы.
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void check()}
          disabled={checking || !native}
          className={cx(btnBase, btn.secondary, btnSize.sm)}
        >
          {checking ? 'Проверка…' : 'Проверить обновление'}
        </button>
        {updateAvailable && (
          <button
            type="button"
            onClick={() => void install()}
            disabled={installing}
            className={cx(btnBase, btn.success, btnSize.sm)}
          >
            {installing ? 'Скачивание…' : `Скачать ${latest?.versionName ?? ''}`}
          </button>
        )}
        {permissionRequired && (
          <button
            type="button"
            onClick={() => void openSettings()}
            className={cx(btnBase, btn.secondary, btnSize.sm)}
          >
            Разрешить установку
          </button>
        )}
      </div>

      {latest && updateAvailable && (
        <div className="mt-3 space-y-1 text-small">
          <div>
            Доступна версия: <strong>{latest.versionName}</strong> (versionCode {latest.versionCode})
          </div>
          <div>Размер: {formatSize(latest.size)}</div>
          <div className="break-all font-mono text-tiny">SHA-256: {latest.sha256}</div>
          {latest.notes && <div>{latest.notes}</div>}
        </div>
      )}

      {message?.tone === 'ok' && <p className={okBanner}>{message.text}</p>}
      {message?.tone === 'err' && <p className={errBanner}>{message.text}</p>}
      {message?.tone === 'hint' && <p className={hint}>{message.text}</p>}
    </div>
  )
}

